import { useQuery } from '@tanstack/react-query';

import { breakpoint } from '@carvana/helios-styles';
import { getUserState } from '@carvana/core-auth';
import Api from '../../api/Api';
import useAnalyticEvent from '../../shared/hooks/useAnalyticEvent';
import { CarouselEventData } from '../../analytics/uiMetrics';
import { useMediaQuery } from '../../shared/hooks/useMediaQuery';
import { AppraisalListContainer, Heading, StyledCarousel } from './AppraisalList.styles';
import { AppraisalSummary } from './summaryShape';
import SummaryTileHelios from './SummaryTile';
import { SummaryTileSkeleton } from './SummaryTileSkeleton';

const AppraisalList = () => {
  const { recordActionClicked } = useAnalyticEvent();
  const { isLoggedIn } = getUserState();
  const isMobile = useMediaQuery(`(max-width: ${breakpoint.sm})`);

  const {
    data: summaries,
    isLoading,
    isError,
    isSuccess
  } = useQuery<AppraisalSummary[]>(['appraisal-summaries'], new Api().appraisal.getSummaries, {
    cacheTime: 1000 * 60 * 60, // one hour
    enabled: isLoggedIn,
    refetchOnWindowFocus: false,
    retry: false,
    staleTime: 1000 * 60 * 30 // 30 minutes
  });

  const sortByDesc = (a: AppraisalSummary, b: AppraisalSummary) =>
    b.expirationDisplayDate.localeCompare(a.expirationDisplayDate);

  const expired =
    summaries?.filter(e => e.status.toLowerCase() === 'expired' && e.isDrivable) || [];
  const active =
    summaries?.filter(
      e =>
        e.status.toLowerCase() !== 'expired' &&
        e.status.toLowerCase() !== 'completed' &&
        e.isDrivable
    ) || [];

  active.sort(sortByDesc);
  expired.sort(sortByDesc);

  const viewable = active.concat(expired);

  if (isError || (isSuccess && (!viewable || viewable.length < 1))) {
    return null;
  }

  const handleCarouselNavigationEvent = (carouselTrackingData: CarouselEventData) => {
    recordActionClicked('Landing Page', 'Carousel', carouselTrackingData);
  };

  return (
    <AppraisalListContainer>
      <StyledCarousel
        carouselHeaderComponent={isMobile ? null : <Heading>My Offers</Heading>}
        carouselAriaLabel="offers"
        trackEvent={handleCarouselNavigationEvent}
      >
        {!viewable.length || isLoading
          ? [1, 2, 3].map(x => <SummaryTileSkeleton key={x} />)
          : viewable.map(summary => (
              <SummaryTileHelios key={summary.appraisalId} summary={summary} />
            ))}
      </StyledCarousel>
    </AppraisalListContainer>
  );
};

export default AppraisalList;
